import React, { useState, useEffect, useCallback } from "react";
import API_BASE from "../apiConfig";
import { useTheme, getT } from "../ThemeContext";
import { useAppConfig } from "../AppContext";
import { useSort } from "../hooks/useSort";
import { useDateTimeFormat } from "../utils/formatters";
import ExcelDownloadButton from "../components/ExcelDownloadButton";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const PIE_COLORS = ["#f7ce0b", "#034e7b", "#0ea5e9", "#22c55e", "#f97316", "#a855f7", "#ef4444", "#14b8a6"];

const money = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(n || 0));

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const STATUS_OPTIONS = ["paid", "partially_paid", "fulfilled", "partially_fulfilled", "refunded", "partially_refunded", "cancelled", "draft", "open"];

function statusLabel(status) {
  return String(status || "").split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

function monthRange(year, month) {
  const start = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;
  const end = isCurrentMonth ? today.toISOString().slice(0, 10) : `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { start, end };
}

const iso = (d) => d.toISOString().slice(0, 10);
const RANGE_PRESETS = ["Today", "This Week", "This Month", "Last 30 Days", "This Year", "Custom"];

function presetRange(preset) {
  const today = new Date();
  if (preset === "Today") return { start: iso(today), end: iso(today) };
  if (preset === "This Week") {
    const d = new Date(today);
    d.setDate(d.getDate() - d.getDay());
    return { start: iso(d), end: iso(today) };
  }
  if (preset === "This Month") return monthRange(today.getFullYear(), today.getMonth() + 1);
  if (preset === "Last 30 Days") {
    const d = new Date(today);
    d.setDate(d.getDate() - 29);
    return { start: iso(d), end: iso(today) };
  }
  if (preset === "This Year") return { start: `${today.getFullYear()}-01-01`, end: iso(today) };
  return null; // Custom — caller keeps whatever month/year is selected
}

export default function POSReports({ currentUser }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { config } = useAppConfig();
  const currency = (config?.currency || "USD").toUpperCase();
  const { fmtDate, fmtDateTime } = useDateTimeFormat();

  const now = new Date();
  const [tab, setTab] = useState("dashboard"); // dashboard | sales | tax

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api("/pos/locations/").then((d) => {
      setLocations(d.locations || []);
      if (d.locations?.length) setLocationId(d.locations[0].location_id);
    }).catch((e) => setError(e.message));
    api("/pos/catalog/categories").then((d) => setCategories(d.categories || [])).catch(() => {});
    api("/pos/catalog/products?active_only=true&page_size=500").then((d) => setProducts(d.products || [])).catch(() => {});
  }, []);

  // ── Dashboard tab state ────────────────────────────────────────────────
  const [dashPreset, setDashPreset] = useState("This Month");
  const [dashMonth, setDashMonth] = useState(now.getMonth() + 1);
  const [dashYear, setDashYear] = useState(now.getFullYear());
  const [dashStart, setDashStart] = useState(() => monthRange(now.getFullYear(), now.getMonth() + 1).start);
  const [dashEnd, setDashEnd] = useState(() => monthRange(now.getFullYear(), now.getMonth() + 1).end);
  const [topN, setTopN] = useState(8);
  const [dashboard, setDashboard] = useState(null);
  const [salesByDay, setSalesByDay] = useState([]);
  const [dashLoading, setDashLoading] = useState(false);

  function applyPreset(preset) {
    setDashPreset(preset);
    const r = presetRange(preset);
    if (r) { setDashStart(r.start); setDashEnd(r.end); }
  }

  useEffect(() => {
    if (dashPreset !== "Custom") return;
    const r = monthRange(dashYear, dashMonth);
    setDashStart(r.start); setDashEnd(r.end);
  }, [dashPreset, dashMonth, dashYear]);

  const { sorted: sortedByDay, toggleSort: toggleDaySort, sortArrow: dayArrow, thSort: dayThSort } =
    useSort(salesByDay, "day", "desc");
  const { sorted: sortedTopProducts, toggleSort: toggleProductSort, sortArrow: productArrow, thSort: productThSort } =
    useSort(dashboard?.top_products || [], "qty_sold", "desc");
  const { sorted: sortedTopCategories, toggleSort: toggleCategorySort, sortArrow: categoryArrow, thSort: categoryThSort } =
    useSort(dashboard?.top_categories || [], "revenue", "desc");
  const { sorted: sortedLowStock, toggleSort: toggleStockSort, sortArrow: stockArrow, thSort: stockThSort } =
    useSort(dashboard?.low_stock || [], "available", "asc");

  const chartTopProducts = sortedTopProducts.slice(0, topN).map((p) => ({ ...p, name: p.description.length > 18 ? p.description.slice(0, 16) + "…" : p.description }));
  const chartTopCategories = sortedTopCategories.slice(0, topN);

  const loadDashboard = useCallback(() => {
    if (!locationId || !dashStart || !dashEnd) return;
    setDashLoading(true); setError("");
    const days = Math.max(1, Math.round((new Date(dashEnd) - new Date(dashStart)) / 86400000) + 1);
    Promise.allSettled([
      api(`/pos/reports/dashboard?location_id=${locationId}&start_date=${dashStart}&end_date=${dashEnd}`),
      api(`/pos/reports/sales-by-day?location_id=${locationId}&days=${days}`),
    ]).then(([d1, d2]) => {
      if (d1.status === "fulfilled") setDashboard(d1.value);
      else setError(d1.reason.message);
      if (d2.status === "fulfilled") setSalesByDay(d2.value.sales || []);
    }).finally(() => setDashLoading(false));
  }, [locationId, dashStart, dashEnd]);

  useEffect(() => { if (tab === "dashboard") loadDashboard(); }, [tab, loadDashboard]);

  // ── Sales report tab state ─────────────────────────────────────────────
  const [salesStart, setSalesStart] = useState(() => monthRange(now.getFullYear(), now.getMonth() + 1).start);
  const [salesEnd, setSalesEnd] = useState(() => now.toISOString().slice(0, 10));
  const [salesCategoryId, setSalesCategoryId] = useState("");
  const [salesProductId, setSalesProductId] = useState("");
  const [salesPaymentMethod, setSalesPaymentMethod] = useState("");
  const [salesStatus, setSalesStatus] = useState("");
  const [salesSearch, setSalesSearch] = useState("");
  const [salesRows, setSalesRows] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);

  const { sorted: sortedSalesRows, toggleSort: toggleSalesSort, sortArrow: salesArrow, thSort: salesThSort } =
    useSort(salesRows, "created_at", "desc");

  const salesParams = useCallback(() => ({
    location_id: locationId, start_date: salesStart || undefined, end_date: salesEnd || undefined,
    category_id: salesCategoryId || undefined, product_id: salesProductId || undefined,
    payment_method: salesPaymentMethod || undefined, status: salesStatus || undefined,
    q: salesSearch || undefined,
  }), [locationId, salesStart, salesEnd, salesCategoryId, salesProductId, salesPaymentMethod, salesStatus, salesSearch]);

  const loadSales = useCallback(() => {
    if (!locationId) return;
    setSalesLoading(true); setError("");
    const qs = new URLSearchParams(
      Object.entries(salesParams()).filter(([, v]) => v !== undefined && v !== "")
    ).toString();
    api(`/pos/reports/sales?${qs}`)
      .then((d) => { setSalesRows(d.rows || []); setSalesSummary(d.summary); })
      .catch((e) => setError(e.message))
      .finally(() => setSalesLoading(false));
  }, [locationId, salesParams]);

  useEffect(() => {
    if (tab !== "sales") return;
    const t = setTimeout(loadSales, 250);
    return () => clearTimeout(t);
  }, [tab, loadSales]);

  // ── Tax report tab state ───────────────────────────────────────────────
  const [taxMonth, setTaxMonth] = useState(now.getMonth() + 1);
  const [taxYear, setTaxYear] = useState(now.getFullYear());
  const [taxReport, setTaxReport] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxError, setTaxError] = useState("");

  const { sorted: sortedTaxRows, toggleSort: toggleTaxSort, sortArrow: taxArrow, thSort: taxThSort } =
    useSort(taxReport?.rows || [], "tax_name");

  const loadTaxReport = useCallback(() => {
    if (!locationId) return;
    setTaxLoading(true); setTaxError("");
    api(`/pos/reports/tax?location_id=${locationId}&year=${taxYear}&month=${taxMonth}`)
      .then((d) => setTaxReport(d))
      .catch((e) => setTaxError(e.message))
      .finally(() => setTaxLoading(false));
  }, [locationId, taxYear, taxMonth]);

  useEffect(() => { if (tab === "tax") loadTaxReport(); }, [tab, loadTaxReport]);

  // ── shared styling ──────────────────────────────────────────────────────
  const card = { background: T.glass, border: `1px solid ${T.glassBdr}`, borderRadius: 14, padding: 16, marginBottom: 20 };
  const tile = { flex: "1 1 160px", minWidth: 150, background: T.glass, border: `1px solid ${T.glassBdr}`, borderRadius: 14, padding: 14 };
  const sel = { padding: "7px 10px", borderRadius: 8, border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, fontSize: 13 };
  const th = { padding: "8px 6px", textAlign: "left", borderBottom: `1px solid ${T.divider}`, color: T.textSub, fontSize: 12 };
  const td = { padding: "8px 6px", borderBottom: `1px solid ${T.divider}`, fontSize: 13 };
  const tabBtn = (active) => ({
    background: active ? T.tabActiveBg : "transparent", color: active ? T.tabActiveClr : T.textSub,
    border: `1px solid ${active ? "transparent" : T.glassBdr}`, borderRadius: 8, padding: "8px 16px",
    fontWeight: 700, fontSize: 13, cursor: "pointer",
  });

  const summary = dashboard?.summary;

  return (
    <div style={{ color: T.textPrimary }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>POS Reports</h2>
        <select style={sel} value={locationId || ""} onChange={(e) => setLocationId(Number(e.target.value))}>
          {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
        </select>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button style={tabBtn(tab === "dashboard")} onClick={() => setTab("dashboard")}>Dashboard</button>
        <button style={tabBtn(tab === "sales")} onClick={() => setTab("sales")}>Sales Report</button>
        <button style={tabBtn(tab === "tax")} onClick={() => setTab("tax")}>Tax Report</button>
      </div>

      {error && <div style={{ color: T.red, marginBottom: 12 }}>{error}</div>}

      {/* ═══════════════════ DASHBOARD ═══════════════════ */}
      {tab === "dashboard" && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            {RANGE_PRESETS.map((p) => (
              <button key={p} style={tabBtn(dashPreset === p)} onClick={() => applyPreset(p)}>{p}</button>
            ))}
            {dashPreset === "Custom" && (
              <>
                <select style={sel} value={dashMonth} onChange={(e) => setDashMonth(Number(e.target.value))}>
                  {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <select style={sel} value={dashYear} onChange={(e) => setDashYear(Number(e.target.value))}>
                  {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </>
            )}
            {dashLoading && <span style={{ color: T.textMuted, fontSize: 12 }}>Loading…</span>}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 12, color: T.textSub }}>{fmtDate(dashStart)} – {fmtDate(dashEnd)}</span>
            <span style={{ width: 1, height: 14, background: T.divider }} />
            <label style={{ fontSize: 12, color: T.textSub, display: "flex", alignItems: "center", gap: 8 }}>
              Show top <input type="range" min={3} max={20} value={topN} onChange={(e) => setTopN(Number(e.target.value))} style={{ verticalAlign: "middle" }} />
              <strong style={{ color: T.textPrimary }}>{topN}</strong>
            </label>
          </div>

          {summary && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
              <div style={tile}>
                <div style={{ fontSize: 11, color: T.textSub }}>Transactions</div>
                <div style={{ fontSize: 22, fontWeight: 900 }}>{summary.transactions}</div>
              </div>
              <div style={tile}>
                <div style={{ fontSize: 11, color: T.textSub }}>Gross Sales</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: T.yellow }}>{money(summary.gross_sales, currency)}</div>
              </div>
              <div style={tile}>
                <div style={{ fontSize: 11, color: T.textSub }}>Discounts</div>
                <div style={{ fontSize: 22, fontWeight: 900 }}>{money(summary.discounts, currency)}</div>
              </div>
              <div style={tile}>
                <div style={{ fontSize: 11, color: T.textSub }}>Tax Collected</div>
                <div style={{ fontSize: 22, fontWeight: 900 }}>{money(summary.tax_collected, currency)}</div>
              </div>
              <div style={tile}>
                <div style={{ fontSize: 11, color: T.textSub }}>Avg Order Value</div>
                <div style={{ fontSize: 22, fontWeight: 900 }}>{money(summary.avg_order_value, currency)}</div>
              </div>
              <div style={tile}>
                <div style={{ fontSize: 11, color: T.textSub }}>Refunds</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: T.red }}>{money(summary.refunds, currency)}</div>
              </div>
            </div>
          )}

          <div style={card}>
            <h3 style={{ marginTop: 0, fontSize: 15 }}>Sales Trend</h3>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={sortedByDay.slice().sort((a, b) => a.day.localeCompare(b.day))}>
                  <defs>
                    <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={T.yellow} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={T.yellow} stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
                  <XAxis dataKey="day" tickFormatter={(d) => fmtDate(d)} tick={{ fontSize: 11, fill: T.textSub }} />
                  <YAxis tick={{ fontSize: 11, fill: T.textSub }} width={70} tickFormatter={(v) => money(v, currency)} />
                  <Tooltip
                    formatter={(v, name) => [name === "total" ? money(v, currency) : v, name === "total" ? "Sales" : "Transactions"]}
                    labelFormatter={(d) => fmtDate(d)}
                    contentStyle={{ background: isDark ? "#0f2436" : "#fff", border: `1px solid ${T.glassBdr}`, borderRadius: 8, fontSize: 12 }}
                  />
                  <Area type="monotone" dataKey="total" stroke={T.yellow} strokeWidth={2} fill="url(#salesFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12, color: T.textSub }}>View as table</summary>
              <div style={{ overflowX: "auto", marginTop: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, ...dayThSort("day", T) }} onClick={() => toggleDaySort("day")}>Date{dayArrow("day")}</th>
                      <th style={{ ...th, ...dayThSort("transactions", T) }} onClick={() => toggleDaySort("transactions")}>Transactions{dayArrow("transactions")}</th>
                      <th style={{ ...th, ...dayThSort("total", T) }} onClick={() => toggleDaySort("total")}>Total{dayArrow("total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedByDay.map((r) => (
                      <tr key={r.day}>
                        <td style={td}>{fmtDate(r.day)}</td>
                        <td style={td}>{r.transactions}</td>
                        <td style={td}>{money(r.total, currency)}</td>
                      </tr>
                    ))}
                    {!sortedByDay.length && (
                      <tr><td colSpan={3} style={{ ...td, color: T.textMuted, textAlign: "center" }}>No sales in this range.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </details>
          </div>

          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {summary?.by_payment_method && Object.keys(summary.by_payment_method).length > 0 && (
              <div style={{ ...card, flex: "1 1 260px" }}>
                <h3 style={{ marginTop: 0, fontSize: 15 }}>Payment Methods</h3>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={Object.entries(summary.by_payment_method).map(([method, total]) => ({ name: method, value: total }))}
                        dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}
                      >
                        {Object.keys(summary.by_payment_method).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => money(v, currency)} contentStyle={{ background: isDark ? "#0f2436" : "#fff", border: `1px solid ${T.glassBdr}`, borderRadius: 8, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11, textTransform: "capitalize" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div style={{ ...card, flex: "2 1 420px" }}>
              <h3 style={{ marginTop: 0, fontSize: 15 }}>Top Products</h3>
              <div style={{ width: "100%", height: Math.max(200, chartTopProducts.length * 28) }}>
                <ResponsiveContainer>
                  <BarChart data={chartTopProducts} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.divider} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: T.textSub }} tickFormatter={(v) => money(v, currency)} />
                    <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: T.textSub }} />
                    <Tooltip formatter={(v) => money(v, currency)} contentStyle={{ background: isDark ? "#0f2436" : "#fff", border: `1px solid ${T.glassBdr}`, borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="revenue" fill={T.yellow} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, color: T.textSub }}>View as table</summary>
                <div style={{ overflowX: "auto", marginTop: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, ...productThSort("description", T) }} onClick={() => toggleProductSort("description")}>Product{productArrow("description")}</th>
                        <th style={{ ...th, ...productThSort("qty_sold", T) }} onClick={() => toggleProductSort("qty_sold")}>Qty{productArrow("qty_sold")}</th>
                        <th style={{ ...th, ...productThSort("revenue", T) }} onClick={() => toggleProductSort("revenue")}>Revenue{productArrow("revenue")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTopProducts.map((p, i) => (
                        <tr key={`${p.product_id}-${i}`}>
                          <td style={td}>{p.description}</td>
                          <td style={td}>{p.qty_sold}</td>
                          <td style={td}>{money(p.revenue, currency)}</td>
                        </tr>
                      ))}
                      {!sortedTopProducts.length && (
                        <tr><td colSpan={3} style={{ ...td, color: T.textMuted, textAlign: "center" }}>No product sales.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>

            <div style={{ ...card, flex: "1 1 320px" }}>
              <h3 style={{ marginTop: 0, fontSize: 15 }}>Top Categories</h3>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={chartTopCategories} dataKey="revenue" nameKey="category" innerRadius={50} outerRadius={85} paddingAngle={2}>
                      {chartTopCategories.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v, currency)} contentStyle={{ background: isDark ? "#0f2436" : "#fff", border: `1px solid ${T.glassBdr}`, borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <details style={{ marginTop: 10 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, color: T.textSub }}>View as table</summary>
                <div style={{ overflowX: "auto", marginTop: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, ...categoryThSort("category", T) }} onClick={() => toggleCategorySort("category")}>Category{categoryArrow("category")}</th>
                        <th style={{ ...th, ...categoryThSort("qty_sold", T) }} onClick={() => toggleCategorySort("qty_sold")}>Qty{categoryArrow("qty_sold")}</th>
                        <th style={{ ...th, ...categoryThSort("revenue", T) }} onClick={() => toggleCategorySort("revenue")}>Revenue{categoryArrow("revenue")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTopCategories.map((c, i) => (
                        <tr key={`${c.category}-${i}`}>
                          <td style={td}>{c.category}</td>
                          <td style={td}>{c.qty_sold}</td>
                          <td style={td}>{money(c.revenue, currency)}</td>
                        </tr>
                      ))}
                      {!sortedTopCategories.length && (
                        <tr><td colSpan={3} style={{ ...td, color: T.textMuted, textAlign: "center" }}>No category sales.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>

            <div style={{ ...card, flex: "1 1 320px" }}>
              <h3 style={{ marginTop: 0, fontSize: 15 }}>Low Stock</h3>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, ...stockThSort("name", T) }} onClick={() => toggleStockSort("name")}>Product{stockArrow("name")}</th>
                      <th style={{ ...th, ...stockThSort("available", T) }} onClick={() => toggleStockSort("available")}>Available{stockArrow("available")}</th>
                      <th style={{ ...th, ...stockThSort("reorder_level", T) }} onClick={() => toggleStockSort("reorder_level")}>Reorder Level{stockArrow("reorder_level")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLowStock.map((p, i) => (
                      <tr key={`${p.name}-${i}`}>
                        <td style={td}>{p.name}</td>
                        <td style={{ ...td, color: T.red, fontWeight: 700 }}>{p.available}</td>
                        <td style={td}>{p.reorder_level}</td>
                      </tr>
                    ))}
                    {!sortedLowStock.length && (
                      <tr><td colSpan={3} style={{ ...td, color: T.textMuted, textAlign: "center" }}>Nothing below reorder level.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════ SALES REPORT ═══════════════════ */}
      {tab === "sales" && (
        <>
          <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
            <div>
              <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>From</label>
              <input type="date" style={sel} value={salesStart} onChange={(e) => setSalesStart(e.target.value)} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>To</label>
              <input type="date" style={sel} value={salesEnd} onChange={(e) => setSalesEnd(e.target.value)} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Category</label>
              <select style={sel} value={salesCategoryId} onChange={(e) => setSalesCategoryId(e.target.value)}>
                <option value="">All categories</option>
                {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Product</label>
              <select style={sel} value={salesProductId} onChange={(e) => setSalesProductId(e.target.value)}>
                <option value="">All products</option>
                {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Payment Method</label>
              <select style={sel} value={salesPaymentMethod} onChange={(e) => setSalesPaymentMethod(e.target.value)}>
                <option value="">All methods</option>
                <option value="cash">Cash</option>
                <option value="stripe">Card (Stripe)</option>
                <option value="store_credit">Store Credit</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Status</label>
              <select style={sel} value={salesStatus} onChange={(e) => setSalesStatus(e.target.value)}>
                <option value="">All (excl. draft/cancelled)</option>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={{ display: "block", fontSize: 11, color: T.textSub, marginBottom: 4 }}>Search order # / customer / item</label>
              <input style={{ ...sel, width: "100%", boxSizing: "border-box" }} value={salesSearch} onChange={(e) => setSalesSearch(e.target.value)} placeholder="Search…" />
            </div>
            <ExcelDownloadButton
              endpoint="/pos/reports/sales/export"
              params={salesParams()}
              filename={`sales_report_${salesStart || "all"}_${salesEnd || "all"}.xlsx`}
              label="Export to Excel"
              disabled={!locationId}
            />
          </div>

          {salesSummary && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "16px 0" }}>
              <div style={tile}><div style={{ fontSize: 11, color: T.textSub }}>Orders</div><div style={{ fontSize: 20, fontWeight: 900 }}>{salesSummary.order_count}</div></div>
              <div style={tile}><div style={{ fontSize: 11, color: T.textSub }}>Items Sold</div><div style={{ fontSize: 20, fontWeight: 900 }}>{salesSummary.total_quantity}</div></div>
              <div style={tile}><div style={{ fontSize: 11, color: T.textSub }}>Gross Sales</div><div style={{ fontSize: 20, fontWeight: 900, color: T.yellow }}>{money(salesSummary.gross_sales, currency)}</div></div>
              <div style={tile}><div style={{ fontSize: 11, color: T.textSub }}>Discount</div><div style={{ fontSize: 20, fontWeight: 900 }}>{money(salesSummary.total_discount, currency)}</div></div>
              <div style={tile}><div style={{ fontSize: 11, color: T.textSub }}>Tax</div><div style={{ fontSize: 20, fontWeight: 900 }}>{money(salesSummary.total_tax, currency)}</div></div>
              <div style={tile}><div style={{ fontSize: 11, color: T.textSub }}>Total Revenue</div><div style={{ fontSize: 20, fontWeight: 900 }}>{money(salesSummary.total_revenue, currency)}</div></div>
            </div>
          )}

          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>Amounts in {currency}. {salesLoading ? "Loading…" : `${sortedSalesRows.length} line item(s).`}</div>

          <div style={{ ...card, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...salesThSort("order_number", T) }} onClick={() => toggleSalesSort("order_number")}>Order #{salesArrow("order_number")}</th>
                  <th style={{ ...th, ...salesThSort("created_at", T) }} onClick={() => toggleSalesSort("created_at")}>Date{salesArrow("created_at")}</th>
                  <th style={{ ...th, ...salesThSort("customer_name", T) }} onClick={() => toggleSalesSort("customer_name")}>Customer{salesArrow("customer_name")}</th>
                  <th style={{ ...th, ...salesThSort("category", T) }} onClick={() => toggleSalesSort("category")}>Category{salesArrow("category")}</th>
                  <th style={{ ...th, ...salesThSort("description", T) }} onClick={() => toggleSalesSort("description")}>Item{salesArrow("description")}</th>
                  <th style={{ ...th, ...salesThSort("quantity", T) }} onClick={() => toggleSalesSort("quantity")}>Qty{salesArrow("quantity")}</th>
                  <th style={{ ...th, ...salesThSort("tax_amount", T) }} onClick={() => toggleSalesSort("tax_amount")}>Tax{salesArrow("tax_amount")}</th>
                  <th style={{ ...th, ...salesThSort("line_total", T) }} onClick={() => toggleSalesSort("line_total")}>Total{salesArrow("line_total")}</th>
                  <th style={{ ...th, ...salesThSort("payment_methods", T) }} onClick={() => toggleSalesSort("payment_methods")}>Payment{salesArrow("payment_methods")}</th>
                  <th style={{ ...th, ...salesThSort("status", T) }} onClick={() => toggleSalesSort("status")}>Status{salesArrow("status")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedSalesRows.map((r) => (
                  <tr key={r.item_id}>
                    <td style={{ ...td, fontWeight: 700 }}>{r.order_number}</td>
                    <td style={td}>{fmtDateTime(r.created_at)}</td>
                    <td style={td}>{r.customer_name}</td>
                    <td style={td}>{r.category}</td>
                    <td style={td}>{r.description}</td>
                    <td style={td}>{r.quantity}</td>
                    <td style={td}>{money(r.tax_amount, currency)}</td>
                    <td style={td}>{money(r.line_total, currency)}</td>
                    <td style={{ ...td, textTransform: "capitalize" }}>{r.payment_methods || "—"}</td>
                    <td style={td}>{statusLabel(r.status)}</td>
                  </tr>
                ))}
                {!salesLoading && !sortedSalesRows.length && (
                  <tr><td colSpan={10} style={{ ...td, color: T.textMuted, textAlign: "center" }}>No sales found for these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════════════ TAX REPORT ═══════════════════ */}
      {tab === "tax" && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>Monthly Tax Report</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select style={sel} value={taxMonth} onChange={(e) => setTaxMonth(Number(e.target.value))}>
                {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select style={sel} value={taxYear} onChange={(e) => setTaxYear(Number(e.target.value))}>
                {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <ExcelDownloadButton
                endpoint="/pos/reports/tax/export"
                params={{ location_id: locationId, year: taxYear, month: taxMonth }}
                filename={`tax_report_${taxYear}-${String(taxMonth).padStart(2, "0")}.xlsx`}
                label="Export for Filing"
                disabled={!locationId}
              />
            </div>
          </div>

          {taxError && <div style={{ color: T.red, marginBottom: 10 }}>{taxError}</div>}
          {taxLoading && <div style={{ color: T.textMuted, marginBottom: 10 }}>Loading…</div>}

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...taxThSort("tax_name", T) }} onClick={() => toggleTaxSort("tax_name")}>Tax Code{taxArrow("tax_name")}</th>
                  <th style={{ ...th, ...taxThSort("rate_pct", T) }} onClick={() => toggleTaxSort("rate_pct")}>Rate{taxArrow("rate_pct")}</th>
                  <th style={{ ...th, ...taxThSort("taxable_sales", T) }} onClick={() => toggleTaxSort("taxable_sales")}>Taxable Sales{taxArrow("taxable_sales")}</th>
                  <th style={{ ...th, ...taxThSort("tax_collected", T) }} onClick={() => toggleTaxSort("tax_collected")}>Tax Collected{taxArrow("tax_collected")}</th>
                  <th style={{ ...th, ...taxThSort("tax_refunded", T) }} onClick={() => toggleTaxSort("tax_refunded")}>Tax Refunded{taxArrow("tax_refunded")}</th>
                  <th style={{ ...th, ...taxThSort("net_tax_due", T) }} onClick={() => toggleTaxSort("net_tax_due")}>Net Tax Due{taxArrow("net_tax_due")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedTaxRows.map((r) => (
                  <tr key={r.tax_name}>
                    <td style={td}>{r.tax_name}</td>
                    <td style={td}>{r.rate_pct}%</td>
                    <td style={td}>{money(r.taxable_sales, currency)}</td>
                    <td style={td}>{money(r.tax_collected, currency)}</td>
                    <td style={{ ...td, color: r.tax_refunded ? T.red : undefined }}>{money(r.tax_refunded, currency)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{money(r.net_tax_due, currency)}</td>
                  </tr>
                ))}
                {!sortedTaxRows.length && (
                  <tr><td colSpan={6} style={{ ...td, color: T.textMuted, textAlign: "center" }}>No taxable sales in this period.</td></tr>
                )}
              </tbody>
              {taxReport?.totals && sortedTaxRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td style={{ ...td, fontWeight: 800, borderBottom: "none" }} colSpan={2}>Total ({taxReport.totals.order_count} orders)</td>
                    <td style={{ ...td, fontWeight: 800, borderBottom: "none" }}>{money(taxReport.totals.taxable_sales, currency)}</td>
                    <td style={{ ...td, fontWeight: 800, borderBottom: "none" }}>{money(taxReport.totals.tax_collected, currency)}</td>
                    <td style={{ ...td, fontWeight: 800, borderBottom: "none" }}>{money(taxReport.totals.tax_refunded, currency)}</td>
                    <td style={{ ...td, fontWeight: 800, borderBottom: "none" }}>{money(taxReport.totals.net_tax_due, currency)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
